import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing FMO configuration queue
 * Handles multiple configurations for batch processing
 */
export const useConfigurationQueue = (
  clusterSelectionMode,
  selectedClusterspec,
  selectedCluster,
  clusterName,
  selectedPocketspec,
  selectedInputspec,
  updateClusterSelectionsFromConfig,
  updateSpecSelectionsFromConfig
) => {
  // Configuration queue states - always start with one active item
  const [configurationQueue, setConfigurationQueue] = useState([{
    id: 1,
    isActive: true,
    clusterSelectionMode: 'clusterspec',
    selectedClusterspec: '',
    selectedCluster: '',
    clusterName: null,
    selectedPocketspec: '',
    selectedInputspec: ''
  }]);
  const [configIdCounter, setConfigIdCounter] = useState(2);
  const [activeConfigId, setActiveConfigId] = useState(1);
  
  // Update active configuration item when selections change
  useEffect(() => {
    if (activeConfigId) {
      setConfigurationQueue(prev => prev.map(config => 
        config.id === activeConfigId 
          ? {
              ...config,
              clusterSelectionMode,
              selectedClusterspec,
              selectedCluster,
              clusterName,
              selectedPocketspec,
              selectedInputspec
            }
          : config
      ));
    }
  }, [activeConfigId, clusterSelectionMode, selectedClusterspec, selectedCluster, clusterName, selectedPocketspec, selectedInputspec]);
  
  // Function to add a new configuration to queue
  const handleAddNewConfiguration = useCallback(() => {
    // Mark current active config as inactive
    const updatedQueue = configurationQueue.map(config => ({
      ...config,
      isActive: false
    }));
    
    // Add new active configuration preserving current values
    const newConfig = {
      id: configIdCounter,
      isActive: true,
      clusterSelectionMode,
      selectedClusterspec,
      selectedCluster,
      clusterName,
      selectedPocketspec,
      selectedInputspec
    };
    
    setConfigurationQueue([...updatedQueue, newConfig]);
    setActiveConfigId(configIdCounter);
    setConfigIdCounter(configIdCounter + 1);
    
    // Don't reset selections - preserve them for the new configuration
    // This allows users to make small changes for variations
  }, [configurationQueue, configIdCounter, clusterSelectionMode, selectedClusterspec, 
      selectedCluster, clusterName, selectedPocketspec, selectedInputspec]);
  
  // Function to check if a configuration is duplicate
  const isDuplicateConfiguration = useCallback((config) => {
    if (!config.selectedPocketspec || !config.selectedInputspec) return false;
    if (config.clusterSelectionMode === 'existing' && !config.selectedCluster) return false;
    if (config.clusterSelectionMode === 'clusterspec' && !config.selectedClusterspec) return false;
    
    return configurationQueue.some(otherConfig => {
      if (otherConfig.id === config.id) return false; // Don't compare with itself
      
      if (config.clusterSelectionMode === 'existing') {
        return otherConfig.clusterSelectionMode === 'existing' &&
               otherConfig.selectedCluster === config.selectedCluster &&
               otherConfig.selectedPocketspec === config.selectedPocketspec &&
               otherConfig.selectedInputspec === config.selectedInputspec;
      } else {
        return otherConfig.clusterSelectionMode === 'clusterspec' &&
               otherConfig.selectedClusterspec === config.selectedClusterspec &&
               otherConfig.selectedPocketspec === config.selectedPocketspec &&
               otherConfig.selectedInputspec === config.selectedInputspec;
      }
    });
  }, [configurationQueue]);
  
  // Function to select a configuration from the queue
  const handleSelectConfiguration = useCallback((configId) => {
    const config = configurationQueue.find(c => c.id === configId);
    if (config) {
      // Update all configs to mark the selected one as active
      setConfigurationQueue(prev => prev.map(c => ({
        ...c,
        isActive: c.id === configId
      })));
      
      setActiveConfigId(configId);
      
      // Load the configuration values into the current selection
      updateClusterSelectionsFromConfig(config);
      updateSpecSelectionsFromConfig(config);
    }
  }, [configurationQueue, updateClusterSelectionsFromConfig, updateSpecSelectionsFromConfig]);
  
  // Function to remove configuration from queue
  const handleRemoveConfiguration = useCallback((configId) => {
    // The UI already prevents removing the last configuration by not showing the delete button
    if (configurationQueue.length === 1) {
      return;
    }
    
    const configToRemove = configurationQueue.find(c => c.id === configId);
    const newQueue = configurationQueue.filter(c => c.id !== configId);
    
    // If we're removing the active config, make another one active
    if (configToRemove?.isActive && newQueue.length > 0) {
      const newActiveConfig = newQueue[newQueue.length - 1];
      newQueue[newQueue.length - 1] = { ...newActiveConfig, isActive: true };
      
      // Load the new active configuration
      handleSelectConfiguration(newActiveConfig.id);
    }
    
    setConfigurationQueue(newQueue);
  }, [configurationQueue, handleSelectConfiguration]);
  
  // Get unique configurations (removing duplicates but keeping one of each)
  const getUniqueConfigurations = useCallback((configs) => {
    const uniqueConfigs = [];
    const seen = new Set();
    
    for (const config of configs) {
      // Skip incomplete configurations
      if (!config.selectedPocketspec || !config.selectedInputspec) continue;
      if (config.clusterSelectionMode === 'existing' && !config.selectedCluster) continue;
      if (config.clusterSelectionMode === 'clusterspec' && !config.selectedClusterspec) continue;
      
      // Create a unique key for this configuration
      const key = config.clusterSelectionMode === 'existing'
        ? `existing-${config.selectedCluster}-${config.selectedPocketspec}-${config.selectedInputspec}`
        : `clusterspec-${config.selectedClusterspec}-${config.selectedPocketspec}-${config.selectedInputspec}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueConfigs.push(config);
      }
    }
    
    return uniqueConfigs;
  }, []);
  
  // Reset queue to initial state
  const resetConfigurationQueue = useCallback(() => {
    setConfigurationQueue([{
      id: 1,
      isActive: true,
      clusterSelectionMode: 'clusterspec',
      selectedClusterspec: '',
      selectedCluster: '',
      clusterName: null,
      selectedPocketspec: '',
      selectedInputspec: ''
    }]);
    setConfigIdCounter(2);
    setActiveConfigId(1);
  }, []);
  
  return {
    // Queue state
    configurationQueue,
    setConfigurationQueue,
    activeConfigId,
    setActiveConfigId,
    
    // Actions
    handleAddNewConfiguration,
    handleSelectConfiguration,
    handleRemoveConfiguration,
    isDuplicateConfiguration,
    getUniqueConfigurations,
    resetConfigurationQueue
  };
};